import { HandLandmarker, DrawingUtils, type Category } from '@mediapipe/tasks-vision';
import { createHandLandmarker } from './helper/createHandLandmarker';

/**
 * 型の設定
 */
export interface Landmark {
  x: number;
  y: number;
  z: number;
  visibility: number;
}
interface DetectionResult {
  landmarksArray: Landmark[][];
  handedness?: Category[][];
}
export type ProcessLandmarkerData = {
  leftIndexFinger: Landmark | null;
  rightIndexFinger: Landmark | null;
};

export class HandLandmarkerApp {
  private video: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private canvasCtx: CanvasRenderingContext2D;
  private drawingUtils: DrawingUtils;
  private handLandmarker?: HandLandmarker; // 手の検出用のLandmarker

  private static readonly INDEX_FINGER_TIP_INDEX = 8; // hand landmarker上の人差し指のインデックス番号
  private lastProcessedData: ProcessLandmarkerData = {
    leftIndexFinger: null,
    rightIndexFinger: null,
  };

  constructor(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
  ) {
    this.video = video;
    this.canvas = canvas;
    this.canvasCtx = canvas.getContext('2d')!;
    this.drawingUtils = new DrawingUtils(this.canvasCtx);
  }

  async initialize(): Promise<void> {
    this.handLandmarker = await createHandLandmarker();
  }

  /**
   * ウェブカメラの映像から骨格を検出し結果を返す
   * @param timestamp
   * @returns
   */
  public detectLandmarker(timestamp: number): DetectionResult | null {
    if (!this.handLandmarker || !this.video.videoWidth) return null;
    this.calcCanvasSize(); // キャンバスの大きさが、ビデオの大きさと違う場合、サイズを一致させる

    // MediaPipeの`detectForVideo`メソッドを呼び出し、姿勢検出の実行
    const results = this.handLandmarker.detectForVideo(this.video, timestamp);

    if (results.landmarks && results.landmarks.length > 0) {
      return {
        landmarksArray: results.landmarks, // 全ランドマークの座標の配列データ
        handedness: results.handedness, // 利き手：left or right
      };
    }
    return null;
  }

  /**
   * 検出結果の生データを受け取り、座標系を変換したデータを返す関数
   * @param detectionResult
   * @returns
   */
  public processLandmarkerData(detectionResult: DetectionResult): ProcessLandmarkerData {
    // 全ランドマークの配列を取得
    const landmarksArray: Landmark[][] = detectionResult.landmarksArray; // 全ランドマークの配列
    const handednessArray: Category[][] = detectionResult.handedness ?? []; // 手の向きの配列

    let leftIndexFinger: Landmark | null = null;
    let rightIndexFinger: Landmark | null = null;

    landmarksArray.forEach((landmarks, index) => {
      if (!landmarks) return;

      const indexFingerLandmark = landmarks[HandLandmarkerApp.INDEX_FINGER_TIP_INDEX];
      if (!indexFingerLandmark) return;

      // "Left" | "Right" を取得し "left" | "right" に正規化
      const classification = handednessArray[index]?.[0]?.categoryName ?? '';
      const handedLabel = classification.toLowerCase();

      if (handedLabel === 'left') {
        leftIndexFinger = indexFingerLandmark;
      } else if (handedLabel === 'right') {
        rightIndexFinger = indexFingerLandmark;
      }
    });

    this.lastProcessedData = {
      leftIndexFinger,
      rightIndexFinger,
    };

    return {
      leftIndexFinger,
      rightIndexFinger,
    };
  }

  /**
   * 検出したKeyPointを2Dキャンバスに描画
   */
  public drawLandmarker(detectionResult: DetectionResult) {
    this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    const landmarksArray = detectionResult.landmarksArray;
    landmarksArray.forEach((landmarks) => {
      // Draw landmarks
      this.drawingUtils.drawLandmarks(landmarks, {
        radius: 3,
        color: '#dc143c',
        lineWidth: 1
      });
      // Draw connections
      this.drawingUtils.drawConnectors(
        landmarks,
        HandLandmarker.HAND_CONNECTIONS,
        {
          color: '#4169e1',
          lineWidth: 2
        }
      );
    })
  }

  // Helper ---------------------------------------------
  /**
   * CanvasとVideoのサイズを一致させる
   */
  private calcCanvasSize() {
    const { width, height } = this.canvas.getBoundingClientRect();
    const displayWidth = Math.round(width);
    const displayHeight = Math.round(height);

    if (!displayWidth || !displayHeight) return;

    if (this.canvas.width !== displayWidth || this.canvas.height !== displayHeight) {
      this.canvas.width = displayWidth;
      this.canvas.height = displayHeight;
    }
  }

}
